import {
  FC,
  HTMLAttributes,
  useRef,
  useState,
  useEffect,
} from "react";
import styled from "styled-components";
import { HudBoard, StageSize, SpinDirection } from "types";
import { Spinner } from "../Spinner";
import { TextStyle } from "pixi.js";
import { useSpring } from "@react-spring/web";
import { GreenBox } from "utils/constants";
import { useSoundStore } from "utils/sound-store";
import { Sounds } from "types/sound";
import { useDebounceCallback } from "usehooks-ts";
import { useTranslation } from "react-i18next";

const Value = styled.div`
  width: 56px;
  height: 22px;
  position: absolute;
  right: 9px;
  top: 2px;

  & > canvas {
    transform-origin: top left;
    transform: scale(0.5);
  }
`;

const Container = styled.div<{ $scale: number, $board: HudBoard; $show: boolean; $isPotValue: boolean }>`
  position: absolute;
  left: calc((100% - 115px) / 2);
  width: 115px;
  height: 26px;
  background-repeat: no-repeat;
  background-size: 100% auto;
  ${(props) => props.$board === 1 ? `
    background-image: url(images/bb-at-risk.png);
    height: 56px;

    ${props.$isPotValue ? '' : 'margin-top: -30px;'}

    & > ${Value} {
      top: 29px;
      left: 50%;
      right: auto;
      transform: translateX(-50%);
    }
  ` : `
    background-image: url(images/hud.webp);
    background-position: 0 ${props.$board * -27}px;
  `}
  visibility: ${(props) => (props.$show ? "visible" : "hidden")};
  transform-origin: top center;
  transform: scale(${props => props.$scale});

  @media (orientation: portrait) {
    left: calc((100% - 148px) / 2);
    width: 148px;
    height: 103px;
    background-image: url(images/hud-mobile.png);
    background-position: 0 ${(props) => props.$board * -103}px;
    transform-origin: bottom center;

    & > ${Value} {
      width: 121px;
      height: 44px;
      right: 14px;
      top: 53px;
    }
  }
`;

const Text = styled(GreenBox)`
  position: absolute;
  left: 108%;
  top: -5px;
  padding: 7px 10px;
  pointer-events: none;

  @media (orientation: portrait) {
    font-size: 2rem;
    top: 18px;
    padding: 13px 25px;
  }
`;

export type ChipCountProps = HTMLAttributes<HTMLDivElement> & {
  board: HudBoard;
  value: string;
  options?: string[];
  textStyle: TextStyle;
  skip: boolean;
  startFrom?: boolean;
  delay?: number;
  scale: number;
  onFinish: () => void;
  isPotValue?: boolean;
};

export const ChipCount: FC<ChipCountProps> = ({
  board,
  value,
  options,
  textStyle,
  skip,
  startFrom,
  delay,
  scale,
  onFinish,
  style,
  isPotValue,
  ...props
}) => {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const [stageSize, setStageSize] = useState<StageSize>({
    width: 0,
    height: 0,
  });
  const [spinTo, setSpinTo] = useState<string>('');
  const optionsRef = useRef<string[]>([]);
  const play = useSoundStore(store => store.play);

  const debSpinTo = useDebounceCallback(() => {
    setSpinTo(value);
  }, !delay ? 0 : delay * 1000);

  const [textSpring, textApi] = useSpring(() => ({
    opacity: 0,
    config: {
      duration: 250,
    },
  }));

  useEffect(() => {
    setStageSize({
      width: (ref.current?.clientWidth || 0) * 2,
      height: (ref.current?.clientHeight || 0) * 2,
    });
  }, []);

  useEffect(() => {
    if(!value) return;

    switch(board){
      case HudBoard.Hand:
        textApi.start({ opacity: 1 });
        break;
      case HudBoard.Lose:
        play(Sounds.Lose);
        break;
      case HudBoard.Win:
        play(Sounds.Win);
        break;
    }
  }, [board, play, textApi, value]);

  useEffect(() => {
    if(skip) textApi.set({ opacity: 0 });
  }, [skip, textApi]);

  useEffect(() => {
    if(startFrom){
      optionsRef.current = optionsRef.current.slice(-1).concat(value);
    }else if(!!options?.length){
      optionsRef.current = [...options];
    }
    debSpinTo();
  }, [debSpinTo, options, startFrom, value]);

  const handleFinish = () => {
    if(!spinTo) return;
    
    if(board === HudBoard.Hand){
      textApi.start({ 
        opacity: 0,
        onRest: onFinish,
      });
    }else{
      onFinish();
    }
  };

  return (
    <Container $isPotValue={!!isPotValue} $scale={scale} $board={board} $show={!!spinTo.trim().length && Number(spinTo.split(' ')[0]) >= 0} style={style}>
      {board !== HudBoard.Blank && <Value ref={ref}>
        <Spinner
          {...stageSize}
          textStyle={textStyle}
          options={optionsRef.current}
          spinTo={spinTo}
          spinSeconds={!value ? -1 : 1}
          spinDirection={
            board === HudBoard.Hand
              ? SpinDirection.Down
              : SpinDirection.Up
          }
          skip={skip}
          onSpinFinish={handleFinish}
        />
      </Value>}
      {board === HudBoard.Hand && <Text style={textSpring}>{t('Finding hand to feature…')}</Text>}
    </Container>
  );
};
